import {
  OUTBOUND_FETCH_RETRIES,
  OUTBOUND_FETCH_RETRY_DELAY_MS,
  OUTBOUND_FETCH_TIMEOUT_MS,
} from '@/core/constants/fetch-resilience';
import { shouldRetryTransientFetchError } from '@/core/utils/is-transient-fetch-error';
import logUniformFetchError from '@/core/utils/log-uniform-fetch-error';
import {
  assertUniformCircuitClosed,
  recordUniformCircuitFailure,
  recordUniformCircuitSuccess,
} from '@/core/utils/uniform-circuit-breaker';

type FetchWithResilienceOptions = {
  label?: string;
  retries?: number;
  retryDelayMs?: number;
  timeoutMs?: number;
  skipCircuitBreaker?: boolean;
};

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

async function fetchWithResilience(
  input: string | URL,
  init?: RequestInit,
  options: FetchWithResilienceOptions = {},
): Promise<Response> {
  const {
    label = 'outbound-fetch',
    retries = OUTBOUND_FETCH_RETRIES,
    retryDelayMs = OUTBOUND_FETCH_RETRY_DELAY_MS,
    timeoutMs = OUTBOUND_FETCH_TIMEOUT_MS,
    skipCircuitBreaker = false,
  } = options;

  if (!skipCircuitBreaker) {
    assertUniformCircuitClosed();
  }

  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(input, {
        ...init,
        signal: AbortSignal.timeout(timeoutMs),
      });

      if (!skipCircuitBreaker) {
        recordUniformCircuitSuccess();
      }

      return response;
    } catch (error) {
      lastError = error;

      if (!skipCircuitBreaker) {
        recordUniformCircuitFailure(error);
      }

      const canRetry =
        attempt < retries && shouldRetryTransientFetchError(error);
      if (!canRetry) {
        logUniformFetchError(label, error, {
          attempt: String(attempt + 1),
          url: String(input),
        });
        throw error;
      }

      logUniformFetchError(label, error, {
        attempt: String(attempt + 1),
        url: String(input),
        retrying: 'true',
      });
      await sleep(retryDelayMs);
    }
  }

  throw lastError;
}

export default fetchWithResilience;
